import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiOpportunity, OpportunityReviewState } from '../../types/aai';
import { Action, Card, Chip, EmptyState, ErrorNote, FactList, Kicker, Loading, Modal, RailCard, RecordPage, useRecordBack } from '../../components/aa';
import type { ChipTone } from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * The Owner's view of one Opportunity.
 *
 * An Opportunity reaches the Owner once the Solution Partner sends it. The
 * Owner's decision is the decision on the request behind it, so confirming,
 * declining and asking for more evidence all run through the request decision
 * and are written back onto the card with who decided, when and why.
 *
 * Confirming approves the business case only. Source access, security,
 * technical design, Workspace membership and release are each approved
 * separately by their owner, and the panel says so on its face.
 */

export const OWNER_STATE_LABEL: Record<OpportunityReviewState, string> = {
  draft: 'With the Solution Partner',
  needs_review: 'With the Solution Partner',
  ready_for_client: 'With the Solution Partner',
  awaiting_client: 'Awaiting your decision',
  confirmed: 'Confirmed',
  declined: 'Declined',
  routed: 'Routed by Orbit',
};

export const OWNER_STATE_TONE: Record<OpportunityReviewState, ChipTone> = {
  draft: 'neutral',
  needs_review: 'neutral',
  ready_for_client: 'neutral',
  awaiting_client: 'warn',
  confirmed: 'ok',
  declined: 'err',
  routed: 'run',
};

export const OWNER_NEXT: Record<OpportunityReviewState, string> = {
  draft: 'The partner is still preparing it',
  needs_review: 'The partner is still preparing it',
  ready_for_client: 'The partner is still preparing it',
  awaiting_client: 'Confirm or decline',
  confirmed: 'Orbit routing',
  declined: 'Nothing further',
  routed: 'Delivery',
};

/*
 * An Opportunity somebody ASKED for is not the same object as one the Solution
 * Partner is preparing, and the partner's vocabulary lies about it: a request
 * sitting at its department head is not "with the Solution Partner". So the
 * request-origin rows read from their own status, and only the partner's rows
 * use the partner's words.
 */
const REQUEST_STATE_LABEL: Record<string, string> = {
  pending: 'With the department head',
  needs_info: 'Sent back for detail',
  dept_approved: 'Awaiting your decision',
  approved: 'Approved',
  declined: 'Declined',
};

const REQUEST_NEXT: Record<string, string> = {
  pending: 'The department head decides first',
  needs_info: 'The person who asked adds detail',
  dept_approved: 'Approve or decline',
  approved: 'Scoping and delivery',
  declined: 'Nothing further',
};

const REQUEST_TONE: Record<string, ChipTone> = {
  pending: 'neutral',
  needs_info: 'warn',
  dept_approved: 'warn',
  approved: 'ok',
  declined: 'err',
};

export const askedFor = (o: AaiOpportunity): boolean => o.source_kind === 'request';

export const stateLabelOf = (o: AaiOpportunity): string => {
  if (askedFor(o) && o.request_display) return o.request_display.chip_label;
  if (askedFor(o)) return REQUEST_STATE_LABEL[o.request_status || ''] || 'Asked for';
  return OWNER_STATE_LABEL[o.review_state];
};

export const stateToneOf = (o: AaiOpportunity): ChipTone => {
  if (askedFor(o)) return REQUEST_TONE[o.request_status || ''] || 'neutral';
  return OWNER_STATE_TONE[o.review_state];
};

export const nextStepOf = (o: AaiOpportunity): string => {
  if (askedFor(o) && o.request_display) return o.request_display.next;
  if (askedFor(o)) return REQUEST_NEXT[o.request_status || ''] || 'With the program team';
  return OWNER_NEXT[o.review_state];
};

export const decisionLine = (o: AaiOpportunity): string | null => {
  if (!o.client_decided_at) return null;
  const who = o.client_decided_by || 'You';
  const when = relativeTime(o.client_decided_at);
  if (o.client_decision_note) return `${who}, ${when}: “${o.client_decision_note}”`;
  return `${who}, ${when}`;
};

type Mode = 'read' | 'decline' | 'evidence';

const FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';
const LABEL = 'mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft';

export const OpportunityDrawer: React.FC<{
  opportunity: AaiOpportunity;
  onClose: () => void;
  onChanged: () => void;
  /** Render as its own page with this back link, instead of a dialog. */
  page?: { backLabel: string };
}> = ({ opportunity, onClose, onChanged, page }) => {
  const [mode, setMode] = useState<Mode>('read');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const awaiting = opportunity.review_state === 'awaiting_client';
  const requestId = opportunity.request_id;

  const decide = async (decision: 'approved' | 'declined' | 'needs_info', done: string) => {
    if (!requestId) return;
    setBusy(true);
    try {
      await aaiApi.decideRequest(requestId, { decision, comment: note.trim() || undefined });
      toast.success(done);
      window.dispatchEvent(new Event('aai:approvals-changed'));
      onChanged();
      setMode('read');
      setNote('');
      if (!page) onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That decision did not save.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => decide('approved', 'Confirmed. Orbit routes the work from here.');
  const decline = () => {
    if (!note.trim()) {
      toast.error('Say why, in a sentence. The partner reads it.');
      return;
    }
    return decide('declined', 'Declined, with your note on the record.');
  };
  const askForEvidence = () => {
    if (!note.trim()) {
      toast.error('Say what evidence you need first.');
      return;
    }
    return decide('needs_info', 'Sent back to the Solution Partner with your question.');
  };

  const noRequestReason = 'This Opportunity has no request to decide on yet';

  const footer = (
    <div className="flex flex-wrap items-center gap-3">
      {awaiting && mode === 'read' && (
        <>
          <Action size="lg" onClick={confirm} disabled={busy || !requestId} reason={requestId ? 'Working' : noRequestReason}>
            Confirm
          </Action>
          <Action size="lg" variant="quiet" onClick={() => setMode('decline')} disabled={busy}>Decline</Action>
          <Action size="lg" variant="quiet" onClick={() => setMode('evidence')} disabled={busy}>Ask a question</Action>
        </>
      )}
      {awaiting && mode === 'decline' && (
        <>
          <Action size="lg" variant="danger" onClick={decline} disabled={busy || !requestId} reason={requestId ? 'Working' : noRequestReason}>
            Decline
          </Action>
          <Action size="lg" variant="ghost" onClick={() => setMode('read')} disabled={busy}>Back</Action>
        </>
      )}
      {awaiting && mode === 'evidence' && (
        <>
          <Action size="lg" onClick={askForEvidence} disabled={busy || !requestId} reason={requestId ? 'Working' : noRequestReason}>
            Send
          </Action>
          <Action size="lg" variant="ghost" onClick={() => setMode('read')} disabled={busy}>Back</Action>
        </>
      )}
      {!awaiting && !page && <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>}
    </div>
  );

  let sub = 'Not a Project yet. Orbit routes it once it is confirmed.';
  if (awaiting) sub = 'The Solution Partner is asking you to confirm this is worth doing.';

  const facts = [
    { label: 'Status', value: OWNER_STATE_LABEL[opportunity.review_state] },
    { label: 'Next step', value: OWNER_NEXT[opportunity.review_state] },
  ];
  const decided = decisionLine(opportunity);
  if (decided) facts.push({ label: 'Your decision', value: decided });

  const noteField = (
    <>
      {mode === 'decline' && (
        <label className="block">
          <span className={LABEL}>Why you are declining</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="One sentence the Solution Partner can act on." />
        </label>
      )}
      {mode === 'evidence' && (
        <label className="block">
          <span className={LABEL}>Your question</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="How many hours a month does this take today, and for whom?" />
        </label>
      )}
    </>
  );

  if (page) {
    return (
      <RecordPage
        backLabel={page.backLabel}
        onBack={onClose}
        kicker={<Kicker>Opportunity</Kicker>}
        badge={<Chip tone={OWNER_STATE_TONE[opportunity.review_state]} dot>{OWNER_STATE_LABEL[opportunity.review_state]}</Chip>}
        title={opportunity.title}
        sub={sub}
        aside={<RailCard title="At a glance"><div className="[&>div>div]:px-5">{<FactList facts={facts} />}</div></RailCard>}
        footer={awaiting && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="m-0 max-w-[52ch] text-[12.5px] leading-[19px] text-ink-dim">
              Confirming only says the idea is worth pursuing. Access, security and release are approved separately.
            </p>
            {footer}
          </div>
        )}
      >
        {opportunity.statement && (
          <Card pad="md">
            <div className={LABEL}>Problem</div>
            <p className="m-0 text-[14px] leading-[23px] text-ink">{opportunity.statement}</p>
          </Card>
        )}
        {opportunity.partner_summary && (
          <Card pad="md">
            <div className={LABEL}>The Solution Partner recommends</div>
            <p className="m-0 text-[14px] leading-[23px] text-ink">{opportunity.partner_summary}</p>
          </Card>
        )}
        {!opportunity.statement && !opportunity.partner_summary && (
          <Card pad="md"><p className="m-0 text-[13.5px] text-ink-dim">No problem statement or recommendation was written for this Opportunity yet.</p></Card>
        )}
        {mode !== 'read' && <Card pad="md">{noteField}</Card>}
      </RecordPage>
    );
  }

  return (
    <Modal
      size="dialog"
      onClose={onClose}
      context={<Kicker>Opportunity</Kicker>}
      badge={<Chip tone={OWNER_STATE_TONE[opportunity.review_state]} dot>{OWNER_STATE_LABEL[opportunity.review_state]}</Chip>}
      footer={(
        <div className="space-y-3">
          {footer}
          {awaiting && mode === 'read' && (
            <p className="m-0 text-[12px] leading-[18px] text-ink-dim">
              Confirming only says the idea is worth pursuing. Access, security and release are approved separately.
            </p>
          )}
        </div>
      )}
    >
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{opportunity.title}</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">{sub}</p>

      {opportunity.statement && (
        <div className="mt-6">
          <div className={LABEL}>Problem</div>
          <p className="m-0 text-[13.5px] leading-[22px] text-ink">{opportunity.statement}</p>
        </div>
      )}

      {opportunity.partner_summary && (
        <Card pad="md" className="mt-5">
          <div className={LABEL}>The Solution Partner recommends</div>
          <p className="m-0 text-[13.5px] leading-[22px] text-ink">{opportunity.partner_summary}</p>
        </Card>
      )}

      <div className="mt-5"><FactList facts={facts} /></div>

      {mode === 'decline' && (
        <label className="mt-5 block">
          <span className={LABEL}>Why you are declining</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="One sentence the Solution Partner can act on." />
        </label>
      )}
      {mode === 'evidence' && (
        <label className="mt-5 block">
          <span className={LABEL}>Your question</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="How many hours a month does this take today, and for whom?" />
        </label>
      )}
    </Modal>
  );
};

/* /projects/opportunities/:id — one Opportunity as its own page. */
export const OpportunityPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { back, label } = useRecordBack('/projects?tab=opportunities', 'Back to Opportunities');
  const [opportunity, setOpportunity] = useState<AaiOpportunity | null | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    aaiApi.listOpportunities()
      .then((res) => setOpportunity(res.data.opportunities.find((o) => o.id === id) || null))
      .catch(() => setFailed(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (opportunity) return <OpportunityDrawer opportunity={opportunity} onClose={back} onChanged={load} page={{ backLabel: label }} />;
  return (
    <RecordPage backLabel={label} onBack={back} kicker={<Kicker>Opportunity</Kicker>} title="Opportunity">
      {failed && <ErrorNote message="Could not open this Opportunity." />}
      {!failed && opportunity === undefined && <Loading label="Opening the Opportunity" />}
      {!failed && opportunity === null && (
        <EmptyState title="This Opportunity is not available" reason="It may have been removed, or it has not been sent to you yet." />
      )}
    </RecordPage>
  );
};
